import { Star as StarIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { isUnsavedContact } from '@sophocles/components/contacts/contactRecordFlags';
import { updateContact } from '@sophocles/features/contacts/api';
import { addToast } from '@sophocles/features/toast';
import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

/**
 * PrimaryContactConfirmDialog - Confirm marking contact as primary
 *
 * Supports 4 primary types:
 * - RMA Billing (is_primary_rma_billing)
 * - RMA Shipping (is_primary_rma_shipping)
 * - Sales Billing (is_primary_sales_billing)
 * - Sales Shipping (is_primary_sales_shipping)
 *
 * Note: Only RMA primaries require complete address (per backend validation).
 *
 * Reusable dialog for confirming primary contact status. Can be used
 * in RMA forms, Contact Management UI, or other contexts.
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {Object} props.contact - Contact to mark as primary (includes contact.primaryType)
 * @param {function} props.onSuccess - Callback when operation succeeds
 */
const PrimaryContactConfirmDialog = ({ open, onClose, contact, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // primaryType: 'rma_billing' | 'rma_shipping' | 'sales_billing' | 'sales_shipping'
  const [primaryType, setPrimaryType] = useState('rma_billing');

  // Initialize primaryType from contact when dialog opens
  useEffect(() => {
    if (contact && contact.primaryType) {
      setPrimaryType(contact.primaryType);
    } else if (contact) {
      // Default to rma_billing if not specified
      setPrimaryType('rma_billing');
    }
  }, [contact]);

  if (!contact) return null;

  // Map primaryType to field name
  const getFieldName = type => {
    const fieldMap = {
      rma_billing: 'is_primary_rma_billing',
      rma_shipping: 'is_primary_rma_shipping',
      sales_billing: 'is_primary_sales_billing',
      sales_shipping: 'is_primary_sales_shipping',
    };
    return fieldMap[type] || 'is_primary_rma_billing';
  };

  // Determine current primary status based on type
  const isCurrentlyPrimary = contact[getFieldName(primaryType)] || false;

  // Check if this is an RMA type (requires address)
  const isRmaType = primaryType.startsWith('rma_');

  // Get human-readable label for primary type
  const getTypeLabel = type => {
    const labels = {
      rma_billing: t('contacts.primary.rmaBilling', 'RMA Billing'),
      rma_shipping: t('contacts.primary.rmaShipping', 'RMA Shipping'),
      sales_billing: t('contacts.primary.salesBilling', 'Sales Billing'),
      sales_shipping: t('contacts.primary.salesShipping', 'Sales Shipping'),
    };
    return labels[type] || type;
  };

  // Handle confirm action
  const handleConfirm = async () => {
    const fieldName = getFieldName(primaryType);
    const newValue = !isCurrentlyPrimary;

    // Address-derived and synthetic picker entries have no saved Contact row —
    // their id ('address-<pk>') is not a Contact pk, so there is no record to
    // mark primary.
    if (isUnsavedContact(contact)) {
      dispatch(
        addToast({
          message: t('contacts.primaryRequiresSavedContact', 'Save this entry as a contact before marking it primary'),
          level: 'warning',
        }),
      );
      onClose();
      return;
    }

    // Validate that contact has complete address and name/business_name before marking as primary
    if (newValue) {
      const hasCompleteName = contact.name || contact.business_name;
      const hasCompleteAddress = contact.address && contact.city && contact.state && contact.zipcode;

      if (!hasCompleteName) {
        dispatch(
          addToast({
            message: t('contacts.primaryRequiresName', 'Primary contacts must have a name or business name'),
            level: 'warning',
          }),
        );
        return;
      }

      // Only require address for RMA primaries
      if (isRmaType && !hasCompleteAddress) {
        dispatch(
          addToast({
            message: t(
              'contacts.primaryRequiresCompleteAddress',
              'RMA Primary contacts must have a complete address (street, city, state, and zip code)',
            ),
            level: 'warning',
          }),
        );
        return;
      }
    }

    try {
      await dispatch(updateContact({ id: contact.id, [fieldName]: newValue })).unwrap();

      const typeLabel = getTypeLabel(primaryType);
      dispatch(
        addToast({
          message: newValue
            ? t('contacts.markPrimarySuccess', `Contact marked as ${typeLabel}`)
            : t('contacts.unmarkPrimarySuccess', `${typeLabel} status removed`),
          level: 'success',
        }),
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      dispatch(
        addToast({ message: t('contacts.markPrimaryError', 'Failed to update primary contact'), level: 'error' }),
      );
    }
  };

  const typeLabel = getTypeLabel(primaryType);

  // Format address for display (fallback if full_address_display not available)
  const formatAddress = () => {
    if (contact.full_address_display) return contact.full_address_display;
    const parts = [
      contact.address,
      contact.address_2,
      [contact.city, contact.state, contact.zipcode].filter(Boolean).join(', '),
      contact.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon color="primary" />
        {isCurrentlyPrimary
          ? t('contacts.primaryDialog.titleUnmark', `Remove ${typeLabel} Primary?`)
          : t('contacts.primaryDialog.titleMark', `Mark as ${typeLabel} Primary?`)}
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {isCurrentlyPrimary
            ? t(
                'contacts.primaryDialog.infoUnmark',
                `This contact will no longer be automatically selected for ${typeLabel.toLowerCase()}.`,
              )
            : t(
                'contacts.primaryDialog.infoMark',
                `This contact will be automatically selected for ${typeLabel.toLowerCase()} when creating new orders and RMAs.`,
              )}
        </Alert>

        {/* Primary Type Selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('contacts.primary.type', 'Primary Type')}</InputLabel>
          <Select
            value={primaryType}
            onChange={e => setPrimaryType(e.target.value)}
            label={t('contacts.primary.type', 'Primary Type')}
          >
            <MenuItem value="rma_billing">{t('contacts.primary.rmaBilling', 'RMA Billing')}</MenuItem>
            <MenuItem value="rma_shipping">{t('contacts.primary.rmaShipping', 'RMA Shipping')}</MenuItem>
            <MenuItem value="sales_billing">{t('contacts.primary.salesBilling', 'Sales Billing')}</MenuItem>
            <MenuItem value="sales_shipping">{t('contacts.primary.salesShipping', 'Sales Shipping')}</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>{contact.name}</strong>
          {contact.business_name && ` (${contact.business_name})`}
        </Typography>

        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 'var(--radius-md)' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('contacts.primaryDialog.email', 'Email')}:</strong> {contact.email || 'N/A'}
          </Typography>
          {contact.phone1 && (
            <Typography variant="body2" color="text.secondary">
              <strong>{t('contacts.primaryDialog.phone', 'Phone')}:</strong> {formatPhoneDisplay(contact.phone1)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <strong>{t('contacts.primaryDialog.address', 'Address')}:</strong>{' '}
            {formatAddress() || t('contacts.primaryDialog.noAddress', 'N/A')}
          </Typography>
        </Box>

        {!isCurrentlyPrimary && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {isRmaType
              ? t(
                  'contacts.primaryDialog.explanationRma',
                  `${typeLabel} contacts are pre-selected when creating new RMAs. They require a complete address.`,
                )
              : t(
                  'contacts.primaryDialog.explanationSales',
                  `${typeLabel} contacts are pre-selected when creating new orders.`,
                )}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isCurrentlyPrimary ? 'warning' : 'primary'}
          startIcon={<StarIcon />}
        >
          {isCurrentlyPrimary
            ? t('contacts.primaryDialog.confirmUnmark', 'Remove Primary')
            : t('contacts.primaryDialog.confirmMark', 'Mark as Primary')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PrimaryContactConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    business_name: PropTypes.string,
    email: PropTypes.string,
    phone1: PropTypes.string,
    address: PropTypes.string,
    address_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
    country: PropTypes.string,
    full_address_display: PropTypes.string,
    is_primary_rma_billing: PropTypes.bool,
    is_primary_rma_shipping: PropTypes.bool,
    is_primary_sales_billing: PropTypes.bool,
    is_primary_sales_shipping: PropTypes.bool,
    primaryType: PropTypes.oneOf(['rma_billing', 'rma_shipping', 'sales_billing', 'sales_shipping']),
  }),
  onSuccess: PropTypes.func,
};

export default PrimaryContactConfirmDialog;
